import gql from 'graphql-tag';
import React from 'react';
import { ApolloQueryResult } from 'apollo-client';
import { Query, Mutation } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { Button } from '../../../components/Bootstrap';
import { Layout, Navigation, LayoutContent, BackButton } from '../../../components';
import { DeliveryLayoutRow, DeliverySeparator, SelectedRow } from '../components';
import { serverAssignmentsFragment, deliveryCategoryFragment, deliveryLayoutFragment } from '../../../fragments';
import { DeliveryLocationFields } from '../../../fragments/__generated__/DeliveryLocationFields';
import {
  AddTablesQuery,
  AddTablesQueryVariables,
  AddTablesQuery_serverAssignments_assignments,
} from './__generated__/AddTablesQuery';
import { UpdateAssignments, UpdateAssignmentsVariables } from './__generated__/UpdateAssignments';
import {
  getStatesForAssignments,
  mapAssignments,
  mapOtherServiceAssignments,
  findLocationsToAddAndRemove,
} from '@getnoble/noble-tip-splitting-utils';

interface AddTablesRouterParams {
  venueId: string;
  fulfillmentCenterId: string;
  serverId: string;
  path?: string;
}

type RouterProps = RouteComponentProps<AddTablesRouterParams>;

interface AddTablesProps extends RouterProps {
  data?: AddTablesQuery;
  loading: boolean;
  error?: Error;
  venueId: string;
  fulfillmentCenterId: string;
  updateAssignments(options: { variables: UpdateAssignmentsVariables });
  refetch(variables: AddTablesQueryVariables): Promise<ApolloQueryResult<AddTablesQuery>>;
}

interface AddTablesState {
  selected: AddTablesQuery_serverAssignments_assignments[];
  hasBeganEditing: boolean;
  path: string[];
}

class AddTablesUnwrapped extends React.Component<AddTablesProps, AddTablesState> {
  state = {
    selected: [],
    path: [],
    hasBeganEditing: false,
  };

  componentDidMount() {}

  componentDidUpdate(prevProps, prevState: AddTablesState) {
    if (prevProps.data !== this.props.data && !this.state.hasBeganEditing) {
      this.setState({
        selected: this.props.data.serverAssignments.assignments,
      });
    } else if (prevState.path !== this.state.path) {
      const { venueId, fulfillmentCenterId, serverId } = this.props.match.params;
      this.props.refetch({
        venueId,
        fulfillmentCenterId,
        serverId,
        parentLocationId: this.state.path[this.state.path.length - 1],
      });
    }
  }

  onToggle = (location: DeliveryLocationFields) => {
    const mappedAssignments = mapAssignments(this.state.selected);
    if (mappedAssignments[location.id]) {
      const selected = this.state.selected.filter((assignment) => {
        if (assignment.location.id === location.id) {
          return false;
        }

        const parentIds = assignment.parents.map((parent) => parent.id);
        return !parentIds.includes(location.id);
      });

      this.setState({ selected, hasBeganEditing: true });
    } else {
      const selected = this.state.selected.concat([
        {
          location,
          parents: this.props.data.fulfillmentCenterDeliveryLayout.parents,
        },
      ]);

      this.setState({ selected, hasBeganEditing: true });
    }
  };

  onClickBack = () => {
    if (this.state.path.length > 0) {
      this.setState({
        path: this.state.path.slice(0, this.state.path.length - 1),
      });
    } else {
      const { venueId, fulfillmentCenterId, serverId } = this.props.match.params;
      const editUrl = `/venue/${venueId}/fulfillment-centers/${fulfillmentCenterId}/assignments/${serverId}/edit`;
      this.props.history.push(editUrl);
    }
  };

  deselectAll = () => {
    this.setState({
      selected: [],
    });
  };

  finish = () => {
    const { locationIdsToAdd, locationIdsToRemove } = findLocationsToAddAndRemove(
      this.props.data.serverAssignments.assignments,
      this.state.selected
    );
    const { venueId, serverId, fulfillmentCenterId } = this.props.match.params;

    this.props.updateAssignments({
      variables: {
        venueId,
        serverId,
        fulfillmentCenterId,
        locationIdsToAdd,
        locationIdsToRemove,
      },
    });
  };

  onClickLocation = (location: DeliveryLocationFields) => {
    const mappedAssignments = mapAssignments(this.state.selected);
    if (mappedAssignments[location.id] === 'current') {
      return;
    }

    const { fulfillmentCenterDeliveryLayout, fulfillmentCenterAssignments } = this.props.data;
    const mappedOtherAssignments = mapOtherServiceAssignments(fulfillmentCenterAssignments);
    if (mappedOtherAssignments[location.id]) {
      return;
    }

    const { parents } = fulfillmentCenterDeliveryLayout;
    const currentPath = parents.map((s) => s.id);
    const path = [...currentPath, location.id];
    this.setState({
      path,
    });
  };

  renderContent() {
    if (this.props.loading || !this.props.data) {
      return <p>Loading...</p>;
    } else {
      const { data } = this.props;
      const { fulfillmentCenter, fulfillmentCenterDeliveryLayout, fulfillmentCenterAssignments } = data;
      if (!fulfillmentCenterDeliveryLayout) {
        return (
          <div className='delivery-assignments'>
            <BackButton onClick={this.onClickBack} />
            <h3 className='mb-2'>{data.serverAssignments.server.name}</h3>
            <div>
              <Button color='primary' onClick={this.finish} className='btn--rounded-padded float-right'>
                Finish
              </Button>
              <Button color='link' className='float-right'>
                Cancel
              </Button>
              <SelectedRow selected={this.state.selected} />
              <p>
                <Button onClick={this.deselectAll} className='p-0' color='link'>
                  Deselect All
                </Button>
              </p>
            </div>
            <DeliverySeparator className='delivery-assignments-separator' />
            <div>No sub-locations</div>
          </div>
        );
      }

      const mappedOtherAssignments = mapOtherServiceAssignments(fulfillmentCenterAssignments);

      const states = getStatesForAssignments(this.state.selected, fulfillmentCenterDeliveryLayout.category.children);

      return (
        <div className='delivery-assignments'>
          <BackButton onClick={this.onClickBack} />
          <h3 className='mb-2'>{data.serverAssignments.server.name}</h3>
          <div>
            <Button color='primary' onClick={this.finish} className='btn--rounded-padded float-right'>
              Save
            </Button>
            <Button color='link' className='float-right'>
              Cancel
            </Button>
            <SelectedRow selected={this.state.selected} />
            <p>
              <Button onClick={this.deselectAll} className='p-0' color='link'>
                Deselect All
              </Button>
            </p>
          </div>
          <DeliverySeparator className='delivery-assignments-separator' />
          <DeliveryLayoutRow
            fulfillmentCenterName={fulfillmentCenter.name}
            locations={fulfillmentCenterDeliveryLayout.category.children}
            selectedStatus={states}
            onToggle={this.onToggle}
            onClickLocation={this.onClickLocation}
            serverStatus={mappedOtherAssignments}
            parentNames={[
              ...fulfillmentCenterDeliveryLayout.parents.map((parent) => parent.name),
              fulfillmentCenterDeliveryLayout.category.adminName,
            ]}
            showHelpText={true}
            onShiftClickOverLocation={this.onToggle}
          />
        </div>
      );
    }
  }

  render() {
    return (
      <Layout>
        <Navigation />
        <Layout.TopBar>
          <Layout.TopBar.Left>
            <Layout.TopBar.Title>Fulfillment Stations</Layout.TopBar.Title>
          </Layout.TopBar.Left>
        </Layout.TopBar>
        <LayoutContent error={this.props.error}>
          <div>{this.renderContent()}</div>
        </LayoutContent>
      </Layout>
    );
  }
}

const query = gql`
  query AddTablesQuery($venueId: ID!, $fulfillmentCenterId: ID!, $serverId: ID!, $parentLocationId: ID) {
    fulfillmentCenter(venueId: $venueId, fulfillmentCenterId: $fulfillmentCenterId) {
      id
      name
    }
    serverAssignments(venueId: $venueId, fulfillmentCenterId: $fulfillmentCenterId, serverId: $serverId) {
      ...ServerAssignmentFields
    }
    fulfillmentCenterDeliveryLayout(
      venueId: $venueId
      fulfillmentCenterId: $fulfillmentCenterId
      parentLocationId: $parentLocationId
    ) {
      category {
        ...DeliveryCategoryFields
      }
      parents {
        ...DeliveryLocationFields
      }
    }
    fulfillmentCenterAssignments(
      venueId: $venueId
      fulfillmentCenterId: $fulfillmentCenterId
      currentServerId: $serverId
    ) {
      id
      location {
        ...DeliveryLocationFields
      }
      server {
        id
        name
      }
    }
  }
  ${serverAssignmentsFragment}
  ${deliveryLayoutFragment}
  ${deliveryCategoryFragment}
`;

const updateAssignemnts = gql`
  mutation UpdateAssignments(
    $venueId: ID!
    $serverId: ID!
    $fulfillmentCenterId: ID!
    $locationIdsToAdd: [ID!]!
    $locationIdsToRemove: [ID!]!
  ) {
    addAssignments(
      venueId: $venueId
      serverId: $serverId
      fulfillmentCenterId: $fulfillmentCenterId
      locationIds: $locationIdsToAdd
    )
    removeAssignments(
      venueId: $venueId
      serverId: $serverId
      fulfillmentCenterId: $fulfillmentCenterId
      locationIds: $locationIdsToRemove
    )
  }
`;

export const AddTables = (props: RouterProps) => {
  const { venueId, fulfillmentCenterId, serverId } = props.match.params;
  const onComplete = React.useCallback(() => {
    const { venueId, fulfillmentCenterId, serverId } = props.match.params;
    const editUrl = `/venue/${venueId}/fulfillment-centers/${fulfillmentCenterId}/assignments/${serverId}/edit`;
    props.history.push(editUrl);
  }, [props.match, props.history]);

  return (
    <Mutation<UpdateAssignments, UpdateAssignmentsVariables> mutation={updateAssignemnts} onCompleted={onComplete}>
      {(updateAssignments, updateAssignmentData) => (
        <Query<AddTablesQuery, AddTablesQueryVariables>
          variables={{ venueId, fulfillmentCenterId, serverId }}
          query={query}
        >
          {({ loading, error, data, refetch }) => {
            return (
              <AddTablesUnwrapped
                {...props}
                loading={loading || updateAssignmentData.loading}
                error={error}
                data={data}
                venueId={venueId}
                fulfillmentCenterId={fulfillmentCenterId}
                refetch={refetch}
                updateAssignments={updateAssignments}
              />
            );
          }}
        </Query>
      )}
    </Mutation>
  );
};
